import React, { useEffect } from "react";
import { motion } from "framer-motion";
import type { DateRange } from "../utils/date"
import {DashboardService} from "../../../../services/dashboard/dashboardService";
import {useState} from "react";
import {FuelLitresPerVehicleRanking} from "../../components/vehicles/FuelLitresPerVehicleRanking";
import {
    AverageConsumptionPerKilometrePerVehicleRanking,
    BackendItemCostKm
} from "../../components/vehicles/AverageConsumptionPerKilometrePerVehicleRanking";
import {
    AccumulatedMaintenanceCostsPerVehicleRanking,
    BackendItemMaintenance
} from "../../components/vehicles/AccumulatedMaintenanceCostsPerVehicleRanking";
import {
    FuelRefuellingPerUserVehicleRanking,
    BackendItemRefuelling,
} from "../../components/vehicles/FuelRefuellingPerUserVehicleRanking";


const fadeUp = {
    hidden: { opacity: 0, y: 12 },
    visible: (i = 1) => ({ opacity: 1, y: 0, transition: { delay: i * 0.06 } }),
};


interface Props {
    range: DateRange;
    refreshKey: number;
}

export const VehiclesStatsTab: React.FC<Props> = ({ range, refreshKey }) => {

    // ===== Litros de combustible por vehículo =======
    const [fuelLitresPerVehicleStats, setFuelLitresPerVehicleStats] = useState<
        { vehicleId: string; plateNumber: string; brand: string; model: string; totalFuelLiters: number | string }[]
    >([]);
    const [loadingFuelRanking, setLoadingFuelRanking] = useState(false);
    const [errorFuelRanking, setErrorFuelRanking] = useState<string | null>(null);

    // ===== Coste del kilometro por vehículo =======
    const [costKmItems, setCostKmItems] = useState<BackendItemCostKm[]>([]);
    const [loadingCostKm, setLoadingCostKm] = useState(false);
    const [errorCostKm, setErrorCostKm] = useState<string | null>(null);

    // ===== Coste de mantenimiento por vehículo =======
    const [maintenanceItems, setMaintenanceItems] = useState<BackendItemMaintenance[]>([]);
    const [loadingMaintenance, setLoadingMaintenance] = useState(false);
    const [errorMaintenance, setErrorMaintenance] = useState<string | null>(null);

    // ===== Repostajes por usuario y vehículo =======
    const [refuellingItems, setRefuellingItems] = useState<BackendItemRefuelling[]>([]);
    const [loadingRefuelling, setLoadingRefuelling] = useState(false);
    const [errorRefuelling, setErrorRefuelling] = useState<string | null>(null);
    
    // Fetch de litros por vehículo
    const fetchFuelLitresPerVehicle = async (r: DateRange) => {
        try {
            setLoadingFuelRanking(true);
            setErrorFuelRanking(null);
            const service = new DashboardService();
            const res = await service.fuelLitresUsedPerVehicle({ time: { start: r.start, end: r.end } });
            const body = (res as any)?.response?.data;
            if (body?.success) {
                setFuelLitresPerVehicleStats(body.data ?? []);
            } else {
                setFuelLitresPerVehicleStats([]);
            }
        } catch (e: any) {
            setErrorFuelRanking(e?.message || "Error obteniendo litros por vehículo");
            setFuelLitresPerVehicleStats([]);
        } finally {
            setLoadingFuelRanking(false);
        }
    };

    // Fetch de coste por km y vehículo
    const fetchCostPerKm = async (r: DateRange) => {
        try {
            setLoadingCostKm(true);
            setErrorCostKm(null);
            const service = new DashboardService();
            const res = await service.averageConsumptionPerKilometrePerVehicle({ time: { start: r.start, end: r.end } });
            const body = (res as any)?.response?.data;
            if (body?.success) setCostKmItems(body.data ?? []);
            else setCostKmItems([]);
        } catch (e: any) {
            setErrorCostKm(e?.message || "Error obteniendo coste por km");
            setCostKmItems([]);
        } finally {
            setLoadingCostKm(false);
        }
    };

    // Fetch de costes de mantenimiento por vehículo
    const fetchMaintenanceCosts = async (r: DateRange) => {
        try {
            setLoadingMaintenance(true);
            setErrorMaintenance(null);
            const service = new DashboardService();
            const res = await service.accumulatedMaintenanceCostsPerVehicle({ time: { start: r.start, end: r.end } });
            const body = (res as any)?.response?.data;
            if (body?.success) setMaintenanceItems(body.data ?? []);
            else setMaintenanceItems([]);
        } catch (e: any) {
            setErrorMaintenance(e?.message || "Error obteniendo costes de mantenimiento");
            setMaintenanceItems([]);
        } finally {
            setLoadingMaintenance(false);
        }
    };

    // Fetch de repostajes por usuario y vehículo
    const fetchRefuellingByUserVehicle = async (r: DateRange) => {
        try {
            setLoadingRefuelling(true);
            setErrorRefuelling(null);
            const service = new DashboardService();
            const res = await service.refuellingPerUserAndVehicle({ time: { start: r.start, end: r.end } });
            const body = (res as any)?.response?.data;
            if (body?.success) setRefuellingItems(body.data ?? []);
            else setRefuellingItems([]);
        } catch (e: any) {
            setErrorRefuelling(e?.message || "Error obteniendo repostajes por usuario y vehículo");
            setRefuellingItems([]);
        } finally {
            setLoadingRefuelling(false);
        }
    };

    const fetchBundle = (r: DateRange) => {
        fetchFuelLitresPerVehicle(r);
        fetchCostPerKm(r);
        fetchMaintenanceCosts(r);
        fetchRefuellingByUserVehicle(r);
    };

    // Dispara al cambiar rango o forzar refresh
    useEffect(() => {
        fetchBundle(range);
    }, [range.start, range.end, refreshKey]);

    return (
        <>
            {/* Ranking de litros de combustible por vehículo */}
            <motion.div variants={fadeUp} initial="hidden" animate="visible" className="h-full">
                <FuelLitresPerVehicleRanking
                    periodLabel={`Periodo: ${range.start} → ${range.end}`}
                    items={fuelLitresPerVehicleStats}
                    loading={loadingFuelRanking}
                    error={errorFuelRanking}
                    height={320}
                    className="h-full"
                    color="#d67611"
                    maxRows={6}
                />
            </motion.div>

            {/* Ranking de coste medio por km y vehículo */}
            <motion.div variants={fadeUp} initial="hidden" animate="visible" className="h-full">
                <AverageConsumptionPerKilometrePerVehicleRanking
                    periodLabel={`Periodo: ${range.start} → ${range.end}`}
                    items={costKmItems}
                    loading={loadingCostKm}
                    error={errorCostKm}
                    height={320}
                    className="h-full"
                    color="#d67611"
                    maxRows={6}
                />
            </motion.div>

            {/* Ranking de costes de mantenimiento por vehículo */}
            <motion.div variants={fadeUp} initial="hidden" animate="visible" className="h-full">
                <AccumulatedMaintenanceCostsPerVehicleRanking
                    periodLabel={`Periodo: ${range.start} → ${range.end}`}
                    items={maintenanceItems}
                    loading={loadingMaintenance}
                    error={errorMaintenance}
                    height={320}
                    className="h-full"
                    color="#d67611"
                    maxRows={6}
                />
            </motion.div>

            {/* Ranking de repostajes por usuario y vehículo */}
            <motion.div variants={fadeUp} initial="hidden" animate="visible" className="h-full">
                <FuelRefuellingPerUserVehicleRanking
                    periodLabel={`Periodo: ${range.start} → ${range.end}`}
                    items={refuellingItems}
                    loading={loadingRefuelling}
                    error={errorRefuelling}
                    height={320}
                    className="h-full"
                    color="#d67611"
                    maxRows={6}
                />
            </motion.div>
        </>
    );
};